import React, { useContext, useEffect, useState } from "react";
import { any, reject, mapObjIndexed, values } from "ramda";

import { ToolContext } from "./ToolProvider";
import Layout from "../Layout";
import Field from "./Field";
import Select from "./Select";
import * as UI from "./ui";
import countries from "../../lib/countries";

import { combineValidations, presence, formatOfIf, exclusionFromIf } from "../../lib/validations";
import { isBlank, isPresent } from "../../lib/presence";

const donorStateValidations = combineValidations([
  presence,
  formatOfIf({
    pattern: /^[A-Za-z]{2}$/,
    message: "Please enter a valid 2 digit State or Province code.",
    predicate: context => context["country"] === "US",
  }),
  exclusionFromIf(
    ["WA"],
    "Not available to residents of the State of Washington.",
    context => context["country"] === "US",
  ),
]);

const validations = {
  firstName: presence,
  lastName: presence,
  email: presence,
  phoneNumber: presence,
  addressLine1: presence,
  city: presence,
  state: donorStateValidations,
  zipCode: presence,
  country: presence,
}

const validate = (values, validations) =>
  reject(isBlank,
    mapObjIndexed((validation, id) =>
      validation && validation(values[id], values), validations)
  )

const DonorForm = ({ disabled, onComplete }) => {
  const { donor, setDonor } = useContext(ToolContext);
  const [formErrors, setFormErrors] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  const validateForm = () => { setFormErrors(validate(donor, validations)) }
  useEffect(validateForm, [donor]);

  const onSubmit = e => {
    e.preventDefault();

    if (any(isPresent, values(formErrors))) {
      setShowAllErrors(true);
      return;
    }

    onComplete();
  };

  const setDonorField = id => e => {
    const value = e.target.value;

    setDonor(current => ({
      ...current,
      [id]: value,
    }));
  };

  return (
    <Layout.FullWidthColumn>
      <UI.SubstepHeading>Enter Your Information</UI.SubstepHeading>
      <UI.Form onSubmit={onSubmit}>
        <UI.FieldGroup>
          <Field id="firstName" label="First Name" onChange={setDonorField("firstName")} value={donor.firstName} errors={formErrors.firstName} touched={showAllErrors}/>
          <Field id="lastName" label="Last Name" onChange={setDonorField("lastName")} value={donor.lastName} errors={formErrors.lastName} touched={showAllErrors}/>
        </UI.FieldGroup>
        <Field type="email" id="email" label="Email" onChange={setDonorField("email")} value={donor.email} errors={formErrors.email} touched={showAllErrors} />
        <Field type="tel" id="phoneNumber" label="Phone" onChange={setDonorField("phoneNumber")} value={donor.phoneNumber} errors={formErrors.phoneNumber} touched={showAllErrors}/>
        <UI.FieldGroup>
          <Field id="addressLine1" label="Address" onChange={setDonorField("addressLine1")} value={donor.addressLine1} weight={3} errors={formErrors.addressLine1} touched={showAllErrors} />
          <Field id="addressLine2" label="Apt #" onChange={setDonorField("addressLine2")} value={donor.addressLine2} weight={2} />
        </UI.FieldGroup>
        <UI.FieldGroup>
          <Field id="city" label="City" onChange={setDonorField("city")} value={donor.city} weight={2} errors={formErrors.city} touched={showAllErrors} />
          <Field id="state" label="State" onChange={setDonorField("state")} value={donor.state} errors={formErrors.state} touched={showAllErrors} persistErrors maxLength={2} />
          <Field id="zipCode" label="Zip" onChange={setDonorField("zipCode")} value={donor.zipCode} errors={formErrors.zipCode} touched={showAllErrors} />
        </UI.FieldGroup>
        <Select id="country" label="Country" onChange={setDonorField("country")} value={donor.country} options={countries} errors={formErrors.country} touched={showAllErrors} />
        <UI.Legal>* By continuing, I certify I am 18 years of age or older, eligible to participate in this sweepstakes, and I agree to abide by the sweepstakes rules.</UI.Legal>
        <UI.FieldGroup>
          <UI.ActionButton value="Continue" disabled={disabled} />
        </UI.FieldGroup>
      </UI.Form>
    </Layout.FullWidthColumn>
  );
};

export default DonorForm;
